/* eslint-disable max-lines -- cohesive pool settings, endpoint list and live statistics */
import { useEffect, useMemo, useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { LLMPoolConfig, LLMPoolEndpoint, LLMPoolStatsResponse } from "@/api/admin-api"

import {
  getLLMPoolConfig,
  getLLMPoolStats,
  refreshLLMPoolConfig,
  updateLLMPoolConfig,
} from "@/api/admin-api"
import { Plus, RefreshCw, Server, Trash2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

import { AdminSettingsLoadState } from "./admin-settings-load-state"
import { CollapsibleManagementSection } from "./collapsible-management-section"
import { RawJsonTab } from "./shared/raw-json-tab"
import { useRawEntityQuery, useSaveRawEntityMutation } from "../hooks/admin-queries"
import { adminQueryKeys } from "../hooks/admin-query-keys"

export function LLMPoolConfigPanel() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [newEndpoint, setNewEndpoint] = useState({
    id: "",
    service_name: "",
    subscription_id: "",
    region: "",
  })
  const [editedSettings, setEditedSettings] = useState<{
    cooldown_seconds?: number
    consecutive_failures_threshold?: number
  }>({})
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const {
    data: config,
    isLoading: configLoading,
    isFetching: configFetching,
    refetch: refetchConfig,
  } = useQuery<LLMPoolConfig>({
    queryKey: adminQueryKeys.llmPoolConfig(),
    queryFn: getLLMPoolConfig,
    staleTime: 60_000,
  })

  const {
    data: stats,
    isLoading: statsLoading,
    isError: statsError,
    isFetching: statsFetching,
    refetch: refetchStats,
  } = useQuery<LLMPoolStatsResponse>({
    queryKey: adminQueryKeys.llmPoolStats(),
    queryFn: getLLMPoolStats,
    staleTime: 10_000,
    refetchInterval: 10_000,
  })

  const refreshConfigMutation = useMutation({
    mutationFn: refreshLLMPoolConfig,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.llmPoolStats() })
    },
  })

  const updateConfigMutation = useMutation({
    mutationFn: updateLLMPoolConfig,
    onSuccess: () => {
      toast.success(t("adminDashboard.llmPool.configSaved", "LLM pool configuration saved"))
      void queryClient.invalidateQueries({ queryKey: adminQueryKeys.llmPoolConfig() })
      setHasUnsavedChanges(false)
      setEditedSettings({})
      // Auto-reload the running balancer after saving
      refreshConfigMutation.mutate()
    },
    onError: () => {
      toast.error(
        t("adminDashboard.llmPool.configUpdateError", "Failed to update LLM pool configuration")
      )
    },
  })

  const cooldownSeconds = editedSettings.cooldown_seconds ?? config?.cooldown_seconds ?? 60
  const failuresThreshold =
    editedSettings.consecutive_failures_threshold ?? config?.consecutive_failures_threshold ?? 3

  function handleSaveSettings() {
    updateConfigMutation.mutate({
      endpoints: config?.endpoints,
      cooldown_seconds: cooldownSeconds,
      consecutive_failures_threshold: failuresThreshold,
    })
  }

  function handleToggleEndpoint(endpointId: string, enabled: boolean) {
    if (!config) return
    const updated = config.endpoints.map((ep) => (ep.id === endpointId ? { ...ep, enabled } : ep))
    updateConfigMutation.mutate({ endpoints: updated })
  }

  function handleDeleteEndpoint(endpointId: string) {
    if (!config) return
    const updated = config.endpoints.filter((ep) => ep.id !== endpointId)
    updateConfigMutation.mutate({ endpoints: updated })
  }

  function handleAddEndpoint() {
    if (!newEndpoint.id || !newEndpoint.service_name) return
    const endpoint: LLMPoolEndpoint = { ...newEndpoint, enabled: true }
    const endpoints = [...(config?.endpoints ?? []), endpoint]
    updateConfigMutation.mutate({ endpoints })
    setAddDialogOpen(false)
    setNewEndpoint({ id: "", service_name: "", subscription_id: "", region: "" })
  }

  const statsMap = new Map((stats?.endpoints ?? []).map((ep) => [ep.id, ep]))

  // Raw JSON tab plumbing (singleton endpoint).
  const rawEndpoint = "/admin/llm-pool/config/raw"
  const { data: rawData, refetch: refetchRaw } =
    useRawEntityQuery<Record<string, unknown>>(rawEndpoint)
  const rawSaveMutation = useSaveRawEntityMutation<Record<string, unknown>>({
    endpoint: rawEndpoint,
    invalidateOnSuccess: [[...adminQueryKeys.llmPoolConfig()]],
  })
  const rawStored = useMemo(() => rawData?.entity ?? {}, [rawData])
  const rawHash = rawData?.hash ?? ""
  const [rawResetSignal, setRawResetSignal] = useState(0)
  useEffect(() => {
    setRawResetSignal((n) => n + 1)
  }, [rawStored])

  const handleRawSave = async (next: unknown, reason: string) => {
    if (!rawHash) return
    const parsed = (next || {}) as Record<string, unknown>
    try {
      await rawSaveMutation.mutateAsync({
        entity: parsed,
        expectedHash: rawHash,
        reason,
        source: "raw",
      })
      void refetchRaw()
      toast.success(
        t("adminDashboard.llmPoolEdit.toastSavedRaw", {
          defaultValue: "LLM pool config saved (raw)",
        })
      )
    } catch (err) {
      toast.error(
        t("adminDashboard.llmPoolEdit.toastRawSaveFailed", { defaultValue: "Raw save failed" }),
        { description: err instanceof Error ? err.message : undefined }
      )
      throw err
    }
  }

  if (!config) {
    return (
      <CollapsibleManagementSection
        icon={<Server className="size-5" />}
        id="llm-pool"
        storageKey="admin-management-llm-pool-expanded"
        title={t("adminDashboard.llmPool.title", "LLM Pool")}
      >
        {configLoading ? (
          <div className="text-muted-foreground py-6 text-center text-sm">
            {t("common.loading", "Loading...")}
          </div>
        ) : (
          <AdminSettingsLoadState retrying={configFetching} onRetry={() => void refetchConfig()} />
        )}
      </CollapsibleManagementSection>
    )
  }

  return (
    <CollapsibleManagementSection
      icon={<Server className="size-5" />}
      id="llm-pool"
      isDirty={hasUnsavedChanges}
      storageKey="admin-management-llm-pool-expanded"
      title={t("adminDashboard.llmPool.title", "LLM Pool")}
      headerActions={
        <div className="flex items-center gap-2">
          <Button
            aria-label={t("common.refresh", "Refresh")}
            disabled={statsFetching}
            size="sm"
            variant="ghost"
            onClick={() => void refetchStats()}
          >
            <RefreshCw className={`size-4 ${statsLoading ? "animate-spin" : ""}`} />
          </Button>
          <Button size="sm" variant="outline" onClick={() => setAddDialogOpen(true)}>
            <Plus className="mr-1 size-4" />
            {t("adminDashboard.llmPool.addEndpoint", "Add Endpoint")}
          </Button>
        </div>
      }
    >
      <Tabs defaultValue="form">
        <TabsList>
          <TabsTrigger value="form">
            {t("adminDashboard.llmPoolEdit.tabForm", { defaultValue: "Form" })}
          </TabsTrigger>
          <TabsTrigger
            className="text-destructive data-[state=active]:text-destructive"
            value="raw"
          >
            {t("adminDashboard.llmPoolEdit.tabRaw", { defaultValue: "Raw JSON" })}
          </TabsTrigger>
        </TabsList>
        <TabsContent className="mt-4" value="form">
          {/* Live Stats */}
          {(statsError || (!stats && !statsLoading)) && (
            <div className="mb-4">
              <AdminSettingsLoadState
                retrying={statsFetching}
                message={
                  stats
                    ? t(
                        "adminDashboard.llmPoolStatsStale",
                        "Pool status could not be refreshed. The values below may be out of date."
                      )
                    : t(
                        "adminDashboard.llmPoolStatsLoadError",
                        "Pool status could not be loaded. Retry to check its health."
                      )
                }
                onRetry={() => void refetchStats()}
              />
            </div>
          )}
          {stats && !stats.enabled && (
            <p className="text-muted-foreground mb-4 text-sm">
              {t(
                "adminDashboard.llmPool.notEnabled",
                "LLM load balancer is not enabled. Configure endpoints below to get started."
              )}
            </p>
          )}

          {(stats?.enabled || statsLoading) && (
            <div className="mb-6 space-y-3">
              <h4 className="text-sm font-medium">
                {t("adminDashboard.llmPool.liveStats", "Live Stats")}
              </h4>
              {/* Header */}
              <div className="text-muted-foreground hidden grid-cols-12 gap-4 px-4 py-2 text-sm font-medium lg:grid">
                <div className="col-span-3">
                  {t("adminDashboard.llmPool.serviceName", "Service")}
                </div>
                <div className="col-span-1">{t("adminDashboard.llmPool.region", "Region")}</div>
                <div className="col-span-2 text-right">
                  {t("adminDashboard.llmPool.requests", "Requests")}
                </div>
                <div className="col-span-1 text-right">
                  {t("adminDashboard.llmPool.failovers", "Failovers")}
                </div>
                <div className="col-span-1 text-right">
                  {t("adminDashboard.llmPool.errors", "Errors")}
                </div>
                <div className="col-span-1 text-right">
                  {t("adminDashboard.llmPool.rateLimited", "429s")}
                </div>
                <div className="col-span-3 text-right">
                  {t("adminDashboard.llmPool.cooldown", "Cooldown")}
                </div>
              </div>
              {/* Rows */}
              {stats?.endpoints.map((ep) => (
                <div
                  key={ep.id}
                  className="admin-responsive-grid-row bg-card hover:bg-accent/50 grid grid-cols-12 items-center gap-4 rounded-lg border p-4 text-sm transition-colors"
                >
                  <div className="col-span-3 truncate font-mono">{ep.service_name}</div>
                  <div className="text-muted-foreground col-span-1 flex items-center gap-3 truncate lg:block">
                    <span className="text-foreground w-24 shrink-0 text-xs font-medium lg:hidden">
                      Region
                    </span>
                    <span>{ep.region}</span>
                  </div>
                  <div className="col-span-2 flex items-center gap-3 font-mono tabular-nums lg:block lg:text-right">
                    <span className="text-muted-foreground w-24 shrink-0 font-sans text-xs font-medium lg:hidden">
                      Requests
                    </span>
                    <span>{ep.total_requests.toLocaleString()}</span>
                  </div>
                  <div className="col-span-1 flex items-center gap-3 font-mono tabular-nums lg:block lg:text-right">
                    <span className="text-muted-foreground w-24 shrink-0 font-sans text-xs font-medium lg:hidden">
                      Failovers
                    </span>
                    <span>{ep.total_failover_requests}</span>
                  </div>
                  <div className="col-span-1 flex items-center gap-3 font-mono tabular-nums lg:block lg:text-right">
                    <span className="text-muted-foreground w-24 shrink-0 font-sans text-xs font-medium lg:hidden">
                      Errors
                    </span>
                    <span>{ep.errors}</span>
                  </div>
                  <div className="col-span-1 flex items-center gap-3 font-mono tabular-nums lg:block lg:text-right">
                    <span className="text-muted-foreground w-24 shrink-0 font-sans text-xs font-medium lg:hidden">
                      429s
                    </span>
                    <span>{ep.rate_limited_count}</span>
                  </div>
                  <div className="col-span-3 flex items-center justify-end">
                    <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
                      Cooldown
                    </span>
                    {ep.in_cooldown ? (
                      <span className="bg-destructive/15 border-destructive text-foreground rounded-full border px-2 py-0.5 text-xs font-medium">
                        {t("adminDashboard.llmPool.inCooldown", "Cooling down")}
                      </span>
                    ) : (
                      <span className="bg-success/15 border-success text-foreground rounded-full border px-2 py-0.5 text-xs font-medium">
                        {t("adminDashboard.llmPool.healthy", "Healthy")}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {!stats?.endpoints.length && !statsLoading && (
                <div className="text-muted-foreground px-4 py-6 text-center text-sm">
                  {t("adminDashboard.llmPool.noStats", "No endpoint stats available")}
                </div>
              )}
            </div>
          )}

          {/* Settings */}
          <div className="mb-6">
            <h4 className="mb-3 text-sm font-medium">
              {t("adminDashboard.llmPool.settings", "Settings")}
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="cooldown-seconds">
                  {t("adminDashboard.llmPool.cooldownSeconds", "Cooldown (seconds)")}
                </Label>
                <Input
                  id="cooldown-seconds"
                  min={0}
                  type="number"
                  value={cooldownSeconds}
                  onChange={(e) => {
                    setEditedSettings((prev) => ({
                      ...prev,
                      cooldown_seconds: Number(e.target.value),
                    }))
                    setHasUnsavedChanges(true)
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="failures-threshold">
                  {t("adminDashboard.llmPool.failuresThreshold", "Consecutive failures threshold")}
                </Label>
                <Input
                  id="failures-threshold"
                  min={1}
                  type="number"
                  value={failuresThreshold}
                  onChange={(e) => {
                    setEditedSettings((prev) => ({
                      ...prev,
                      consecutive_failures_threshold: Number(e.target.value),
                    }))
                    setHasUnsavedChanges(true)
                  }}
                />
              </div>
            </div>
            {hasUnsavedChanges && (
              <Button
                className="mt-3"
                disabled={updateConfigMutation.isPending}
                size="sm"
                onClick={handleSaveSettings}
              >
                {t("adminDashboard.llmPool.saveSettings", "Save Settings")}
              </Button>
            )}
          </div>

          {/* Endpoints */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium">
              {t("adminDashboard.llmPool.endpoints", "Endpoints")}
            </h4>
            {/* Header */}
            <div className="text-muted-foreground hidden grid-cols-12 gap-4 px-4 py-2 text-sm font-medium lg:grid">
              <div className="col-span-3">{t("adminDashboard.llmPool.serviceName", "Service")}</div>
              <div className="col-span-3">
                {t("adminDashboard.llmPool.subscriptionId", "Subscription")}
              </div>
              <div className="col-span-2">{t("adminDashboard.llmPool.region", "Region")}</div>
              <div className="col-span-2 text-right">
                {t("adminDashboard.llmPool.enabled", "Enabled")}
              </div>
              <div className="col-span-2 text-right">
                {t("adminDashboard.llmPool.cooldown", "Status")}
              </div>
            </div>
            {/* Rows */}
            {config.endpoints.map((ep) => {
              const epStats = statsMap.get(ep.id)
              let statusBadge: React.ReactNode

              if (epStats?.in_cooldown) {
                statusBadge = (
                  <span className="bg-destructive/15 border-destructive text-foreground rounded-full border px-2 py-0.5 text-xs font-medium">
                    {t("adminDashboard.llmPool.inCooldown", "Cooling down")}
                  </span>
                )
              } else if (ep.enabled) {
                statusBadge = (
                  <span className="bg-success/15 border-success text-foreground rounded-full border px-2 py-0.5 text-xs font-medium">
                    {t("adminDashboard.llmPool.statusActive", "Active")}
                  </span>
                )
              } else {
                statusBadge = (
                  <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs font-medium">
                    {t("adminDashboard.llmPool.statusDisabled", "Disabled")}
                  </span>
                )
              }

              return (
                <div
                  key={ep.id}
                  className="admin-responsive-grid-row bg-card hover:bg-accent/50 grid grid-cols-12 gap-4 rounded-lg border p-4 text-sm transition-colors"
                >
                  <div className="col-span-3 flex flex-col">
                    <span className="truncate font-medium">{ep.service_name}</span>
                    <span className="text-muted-foreground truncate text-xs">{ep.id}</span>
                  </div>
                  <div className="text-muted-foreground col-span-3 flex items-center gap-3 truncate font-mono text-xs lg:block">
                    <span className="text-foreground w-24 shrink-0 font-sans font-medium lg:hidden">
                      Subscription
                    </span>
                    <span>{ep.subscription_id}</span>
                  </div>
                  <div className="text-muted-foreground col-span-2 flex items-center gap-3 lg:block">
                    <span className="text-foreground w-24 shrink-0 text-xs font-medium lg:hidden">
                      Region
                    </span>
                    <span>{ep.region}</span>
                  </div>
                  <div className="col-span-2 flex items-center justify-end">
                    <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
                      Enabled
                    </span>
                    <Switch
                      aria-label={`${ep.service_name} enabled`}
                      checked={ep.enabled}
                      onCheckedChange={(checked) => handleToggleEndpoint(ep.id, checked)}
                    />
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
                      Status
                    </span>
                    {statusBadge}
                    <Button
                      aria-label={t("adminDashboard.llmPool.deleteEndpoint", "Delete endpoint")}
                      className="hover:bg-destructive hover:text-destructive-foreground"
                      size="icon"
                      variant="outline"
                      onClick={() => handleDeleteEndpoint(ep.id)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
              )
            })}
            {!config.endpoints.length && (
              <div className="text-muted-foreground px-4 py-6 text-center text-sm">
                {t("adminDashboard.llmPool.noEndpoints", "No endpoints configured")}
              </div>
            )}
          </div>
        </TabsContent>
        <TabsContent className="mt-4" value="raw">
          <RawJsonTab
            confirmToken="llm-pool"
            isPending={rawSaveMutation.isPending || !rawHash}
            resetSignal={rawResetSignal}
            value={rawStored}
            resourceLabel={t("adminDashboard.llmPoolEdit.rawResourceLabel", {
              defaultValue: "LLM pool config",
            })}
            onSave={handleRawSave}
          />
        </TabsContent>
      </Tabs>

      {/* Add Endpoint Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("adminDashboard.llmPool.addEndpointTitle", "Add LLM Endpoint")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-ep-id">{t("adminDashboard.llmPool.id", "ID")}</Label>
              <Input
                id="new-ep-id"
                placeholder="e.g. sub2-swedencentral"
                value={newEndpoint.id}
                onChange={(e) => setNewEndpoint((prev) => ({ ...prev, id: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-ep-service">
                {t("adminDashboard.llmPool.serviceName", "Service")}
              </Label>
              <Input
                id="new-ep-service"
                placeholder="e.g. my-openai-service"
                value={newEndpoint.service_name}
                onChange={(e) =>
                  setNewEndpoint((prev) => ({ ...prev, service_name: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-ep-sub">
                {t("adminDashboard.llmPool.subscriptionId", "Subscription")}
              </Label>
              <Input
                id="new-ep-sub"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={newEndpoint.subscription_id}
                onChange={(e) =>
                  setNewEndpoint((prev) => ({ ...prev, subscription_id: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-ep-region">{t("adminDashboard.llmPool.region", "Region")}</Label>
              <Input
                id="new-ep-region"
                placeholder="e.g. swedencentral"
                value={newEndpoint.region}
                onChange={(e) => setNewEndpoint((prev) => ({ ...prev, region: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              disabled={!newEndpoint.id || !newEndpoint.service_name}
              onClick={handleAddEndpoint}
            >
              {t("adminDashboard.llmPool.add", "Add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CollapsibleManagementSection>
  )
}
